/**
 * emamaco size guide — maternity and pregnancy-recovery.
 *
 * Replaces the Convomesh sizing widget (a 257KB minified React bundle behind a
 * srcdoc iframe). Listens on the same `convomesh-open-sizing` event, so the
 * existing Size Guide link and stage-set.js both keep working untouched.
 *
 * TWO FAMILIES, genuinely different rules — mum-life is still excluded and
 * gets the chart only.
 *
 * MATERNITY: hip bands OVERLAP at every boundary, which is the "everyone fits
 * 2 sizes" rule as data. Pair = [size, size-1], snug leads.
 *
 * RECOVERY: a different chart with a WAIST column maternity has no equivalent
 * for, and contiguous (non-overlapping) hip bands. Two paths:
 *   • owns emamaco maternity  -> one size UP from it; her maternity size stays
 *     available as the brand's own "super tight fit" option. This is the
 *     sizeRule 'up1' the theme already carries for recovery bottoms.
 *   • by measurement -> 35+ weeks pregnant uses hip alone; postpartum takes
 *     whichever of waist or hip lands in the LARGER size category.
 * The second option INVERTS versus maternity: the recovery page says outright
 * "do not size down as these are a compression garment and quite tight", so
 * the chart path never offers a smaller size — only "less tight fit" one up.
 *
 * WHY IT RECOMMENDS THE WAY IT DOES
 * The chart overlaps at almost every boundary — hip 100 is both XS and S,
 * AUS 8 is both XS and S — which is the "everyone fits 2 sizes" rule from the
 * sizing page expressed as data. So the job is never "find your size", it is
 * "you fit two, here they are, snug first".
 *
 * Snug leads because 22,872 returns say so. There are no structured return
 * reasons in the data, but of 1,532 maternity returns carrying a free-text
 * note, 337 read as too small against 61 as too big — 5.5:1, and it holds at
 * every size (XS 9.4:1, S 7.7:1, M 4.2:1, L 2.7:1). Leggings skew less hard
 * than shorts (4.7:1 vs 9.1:1), which is the roll-down effect showing up in
 * the data, but too-small still dominates even there. Hence: both sizes are
 * always shown with the page's own wording, and the cursor starts on snug.
 *
 * Copy is lifted from /pages/copy-of-maternity-sizing-information so the sheet
 * and the page say the same thing in the same voice.
 */
(function () {
  var SIZES = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

  // Hip bands in cm, straight off the published chart. Upper bounds are
  // inclusive and shared with the next band up — that overlap IS the two-size
  // rule, so a boundary value deliberately resolves to two sizes.
  var HIP = {
    XXS: [80, 90], XS: [90, 100], S: [100, 110], M: [110, 120],
    L: [120, 130], XL: [130, 135], XXL: [135, 140], XXXL: [140, 999]
  };
  // Dress sizes. AUS/UK and US/CAN columns of the same chart. Note the three
  // junctions with no overlap (AUS 10|12, 18|20, 22|24): real AUS sizes are
  // even, so nothing is missing — there is simply no shared size there, and a
  // value landing in the gap resolves upward (see sizeFromDress).
  var AUS = {
    XXS: [4, 6], XS: [6, 8], S: [8, 10], M: [12, 14],
    L: [14, 16], XL: [16, 18], XXL: [20, 22], XXXL: [24, 26]
  };
  var US = {
    XXS: [0, 2], XS: [2, 4], S: [4, 6], M: [8, 10],
    L: [10, 12], XL: [12, 14], XXL: [16, 18], XXXL: [20, 22]
  };

  // ── Recovery chart (pages/pregnancy-recovery-size-charts) ────────────────
  // Hip bands here are CONTIGUOUS, not overlapping like maternity's, so the
  // second size comes from the size-up rule rather than from the table.
  // US sizes are single values at XS/S/M, unlike maternity's ranges.
  var REC_HIP = {
    XXS: [80, 86], XS: [87, 93], S: [94, 99], M: [100, 108],
    L: [109, 117], XL: [118, 126], XXL: [127, 138], XXXL: [139, 999]
  };
  var REC_WAIST = {
    XXS: [65, 70], XS: [70, 75], S: [75, 80], M: [80, 85],
    L: [85, 90], XL: [90, 95], XXL: [95, 100], XXXL: [100, 110]
  };
  var REC_AUS = {
    XXS: [4, 6], XS: [6, 8], S: [8, 10], M: [12, 14],
    L: [14, 16], XL: [16, 18], XXL: [20, 22], XXXL: [24, 26]
  };
  var REC_US = {
    XXS: [0, 2], XS: [4, 4], S: [6, 6], M: [8, 8],
    L: [10, 10], XL: [12, 14], XXL: [16, 18], XXXL: [20, 22]
  };

  // ── Crops / sports bras / nursing tops ───────────────────────────────────
  // (pages/crop-tops-and-sports-bra-size-guide)
  // Structurally different again: single target values rather than bands, only
  // XS-XXL, and a CUP CAP column that acts as a second constraint rather than
  // an input. Underbust values sit 5cm apart, so a measurement resolves to the
  // NEAREST value with ties going up.
  var CROP_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
  var CROP_UNDERBUST = { XS: 75, S: 80, M: 85, L: 90, XL: 95, XXL: 100 };
  var CROP_BUST      = { XS: 85, S: 90, M: 95, L: 100, XL: 105, XXL: 110 };
  // Largest cup each size holds. A bigger cup than her underbust size allows
  // is what puts a small-framed, busty shopper in the wrong size — the returns
  // notes are full of exactly that ("narrower frame around the ribcage but
  // busty", "too small in cup"). So cup sets a FLOOR and the larger wins.
  var CROP_CUP_CAP = { XS: 'DD', S: 'E', M: 'F', L: 'F', XL: 'F', XXL: 'F' };
  var CUPS = ['A', 'B', 'C', 'D', 'DD', 'E', 'F', 'G+'];
  var CUP_FLOOR = { A: 'XS', B: 'XS', C: 'XS', D: 'XS', DD: 'XS', E: 'S', F: 'M' };

  // ── Shaper Move + all other leggings and shorts ──────────────────────────
  // (pages/shaper-sizing-information)
  // A hybrid of the other two charts: maternity's OVERLAPPING hip bands with
  // recovery's waist bands, and no XXXL row. Its two-size rule is also its own
  // — "2 sizes will always fit every one depending if you like a super tight
  // fit or a tight fit" — so BOTH options are tight here, unlike recovery
  // where the alternative is roomier.
  var GEN_SIZES = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL'];
  var GEN_HIP = {
    XXS: [80, 90], XS: [90, 100], S: [100, 110], M: [110, 120],
    L: [120, 130], XL: [130, 135], XXL: [135, 140]
  };
  var GEN_WAIST = {
    XXS: [65, 70], XS: [70, 75], S: [75, 80], M: [80, 85],
    L: [85, 90], XL: [90, 95], XXL: [95, 100]
  };
  var GEN_AUS = {
    XXS: [4, 6], XS: [6, 8], S: [8, 10], M: [12, 14],
    L: [14, 16], XL: [16, 18], XXL: [20, 22]
  };
  var GEN_US = {
    XXS: [0, 2], XS: [4, 4], S: [6, 6], M: [8, 8],
    L: [10, 10], XL: [12, 14], XXL: [16, 18]
  };

  var VIDEO = {
    // Uploaded to Shopify Files 17 Sep — replaces the YouTube iframe the
    // sizing page uses. 480p is plenty for a phone sub-modal.
    src: 'https://cdn.shopify.com/videos/c/vp/f108ae5c16ed425dae3426f0587f573f/f108ae5c16ed425dae3426f0587f573f.SD-480p-0.9Mbps-94655839.mp4',
    poster: 'https://cdn.shopify.com/s/files/1/0117/6305/6699/files/preview_images/f108ae5c16ed425dae3426f0587f573f.thumbnail.0000000000.jpg?v=1789601780'
  };

  // Same country test the theme already uses for mum/mom, so units and the
  // dress-size column switch together and stay consistent with the copy.
  function isUS() {
    var c = window.Shopify && window.Shopify.country && window.Shopify.country.iso_code;
    return c === 'US' || c === 'CA';
  }
  function unit() { return isUS() ? 'in' : 'cm'; }
  function dressTable() { return isUS() ? US : AUS; }
  function toCm(v) { return isUS() ? v * 2.54 : v; }

  var idx = function (s) { return SIZES.indexOf(s); };
  var down = function (s) { return SIZES[Math.max(0, idx(s) - 1)]; };

  /** Highest band containing the measurement — the snug half of an overlap. */
  function sizeFromHip(cm) {
    if (!cm) return null;
    if (cm < HIP.XXS[0]) return 'XXS';
    for (var i = SIZES.length - 1; i >= 0; i--) {
      var b = HIP[SIZES[i]];
      if (cm >= b[0] && cm <= b[1]) return SIZES[i];
    }
    return 'XXXL';
  }

  /** Same, for a dress size. A value in a junction gap resolves upward. */
  function sizeFromDress(n) {
    var T = dressTable();
    if (!n && n !== 0) return null;
    for (var i = SIZES.length - 1; i >= 0; i--) {
      var b = T[SIZES[i]];
      if (n >= b[0] && n <= b[1]) return SIZES[i];
    }
    for (var j = 0; j < SIZES.length; j++) if (n < T[SIZES[j]][0]) return SIZES[j];
    return 'XXXL';
  }

  var up = function (sz) { return SIZES[Math.min(SIZES.length - 1, idx(sz) + 1)]; };

  /**
   * Band lookup over an arbitrary table; highest match wins an overlap.
   *
   * Derives its size list from the TABLE, not the global SIZES. The general
   * (shaper) chart stops at XXL, so walking all eight sizes dereferenced an
   * undefined band and threw — which silently killed the whole render, and
   * the measurement path just produced nothing.
   */
  function sizeFromBand(table, v) {
    if (!(v > 0)) return null;
    var list = SIZES.filter(function (sz) { return !!table[sz]; });
    if (!list.length) return null;
    if (v < table[list[0]][0]) return list[0];
    for (var i = list.length - 1; i >= 0; i--) {
      var b = table[list[i]];
      if (v >= b[0] && v <= b[1]) return list[i];
    }
    return list[list.length - 1];
  }

  /** Nearest underbust value, ties up. */
  function sizeFromUnderbust(cm) {
    if (!(cm > 0)) return null;
    var best = CROP_SIZES[0], bestD = Infinity;
    CROP_SIZES.forEach(function (sz) {
      var d = Math.abs(CROP_UNDERBUST[sz] - cm);
      if (d < bestD || (d === bestD && CROP_UNDERBUST[sz] > cm)) { bestD = d; best = sz; }
    });
    return best;
  }

  function isCropProduct() {
    var g = document.getElementById('pdp-gallery');
    var name = ((g && g.getAttribute('data-product-name')) || '').toLowerCase();
    var title = ((document.getElementById('v2-title-name') || {}).textContent || '').toLowerCase();
    var hay = name + ' ' + title;
    if (/legging|short|brief|underwear|pant/.test(hay)) return false;   // bottoms are never this chart
    if (/\btee\b/.test(hay)) return false;   // a tee, even the Cropped Tee, isn't sized by underbust and cup
    return /crop|bra\b|singlet|tank|nursing|\btop\b/.test(hay);
  }

  /**
   * Body Shapewear. Its published chart is byte-identical to the recovery one
   * (same waist, hip, AUS and US columns) and its guidance is word-for-word
   * the same — waist and hip, larger wins, size up for a less tight fit, never
   * down. So it reuses the REC_* tables and only the view differs: no
   * pregnancy stage, no "one up from your maternity" path.
   *
   * The page is explicit that "The Shaper Move range has it's own size chart",
   * so shaper-move is excluded here and still falls through to chart-only.
   */
  /**
   * Everything else with a waist and hips: Shaper Move, and any other leggings
   * or shorts that aren't maternity, recovery or body shapewear. Mostly a
   * confidence tool — the honest answer is usually "your usual size" — with
   * measurements available for anyone who wants to be sure.
   */
  function isGeneralBottomProduct() {
    var g = document.getElementById('pdp-gallery');
    var name = ((g && g.getAttribute('data-product-name')) || '').toLowerCase();
    var title = ((document.getElementById('v2-title-name') || {}).textContent || '').toLowerCase();
    var hay = name + ' ' + title;
    return /legging|short|shaper|tight|pant/.test(hay);
  }

  function isShapewearProduct() {
    var g = document.getElementById('pdp-gallery');
    var name = ((g && g.getAttribute('data-product-name')) || '').toLowerCase();
    var title = ((document.getElementById('v2-title-name') || {}).textContent || '').toLowerCase();
    var hay = name + ' ' + title;
    if (/shaper.?move/.test(hay)) return false;
    return /shapewear/.test(hay);
  }

  function isRecoveryProduct() {
    if (isCropProduct()) return false;   // a recovery CROP is still the crop chart
    var g = document.getElementById('pdp-gallery');
    var name = ((g && g.getAttribute('data-product-name')) || '').toLowerCase();
    var title = ((document.getElementById('v2-title-name') || {}).textContent || '').toLowerCase();
    var hay = name + ' ' + title;
    return /recovery|post ?partum/.test(hay);
  }

  function isMaternityProduct() {
    var g = document.getElementById('pdp-gallery');
    var name = ((g && g.getAttribute('data-product-name')) || '').toLowerCase();
    var title = (document.getElementById('v2-title-name') || {}).textContent || '';
    var ls = (window.pdpMeta && window.pdpMeta.pdpLifestage) || window.pdpLifestage || '';
    if (isRecoveryProduct()) return false;
    if (ls === 'pregnant') return true;
    return /maternity/.test(name) || /maternity/i.test(title);
  }

  /** The shopper's current pick on the PDP, so the sheet opens pre-filled. */
  function pdpSelectedSize() {
    var b = document.querySelector('#size-buttons button.selected');
    var t = b && b.textContent.trim().toUpperCase();
    return SIZES.indexOf(t) >= 0 ? t : null;
  }

  function selectOnPdp(size) {
    var btns = document.querySelectorAll('#size-buttons button');
    for (var i = 0; i < btns.length; i++) {
      if ((btns[i].textContent || '').trim().toUpperCase() === size) { btns[i].click(); return true; }
    }
    return false;
  }
  function inStockOnPdp(size) {
    var btns = document.querySelectorAll('#size-buttons button');
    for (var i = 0; i < btns.length; i++) {
      if ((btns[i].textContent || '').trim().toUpperCase() === size) return !btns[i].disabled;
    }
    return true;   // no size row on this surface — don't invent a stock story
  }

  // ── state ──────────────────────────────────────────────────────────────
  var st = null;   // maternity: {mode:'letter'|'dress', letter, dress, hip, pick}
                   // recovery:  {mode:'mat'|'measure', matSize, stage, hip, waist, pick}
  var root = null;
  var FAMILY = 'maternity';   // 'maternity' | 'recovery' | 'other'

  /**
   * The two sizes she'll fit, snug first.
   *
   * Where the hip measurement and what she says she wears disagree, the pair
   * BRACKETS them rather than one overriding the other: snug is the larger,
   * tighter is the smaller. Two reasons. Her stated size is real information
   * (weak, since vanity sizing varies by brand, but real), and offering XS to
   * someone who just told us she wears M is obviously wrong. And with returns
   * running 5.5:1 too small, resolving a disagreement downward is the one
   * direction the data says not to take.
   *
   * When the two agree — or only one is known — the pair is that size and the
   * one below it, which is the sizing page's own example (hip 100 + size 8 ->
   * XS tighter, S snug).
   */
  /**
   * Recovery. Two paths, and the second option differs from maternity.
   *
   *  maternity-size path — "one size up from your current emamaco maternity
   *    leggings unless you want a super tight fit". The tight option here is
   *    HER OWN maternity size, which the brand sanctions explicitly; that is
   *    not the same as sizing down off the chart, which it forbids.
   *
   *  measurement path — 35+ weeks pregnant: hip alone. Postpartum: whichever
   *    of waist or hip lands in the LARGER category. Second option is one UP,
   *    "for a less tight fit". Never smaller.
   */
  function computedRecovery() {
    // Mode-gated: a maternity size entered earlier must not keep overriding
    // the measurements once she switches to that path.
    if (FAMILY !== 'shapewear' && st.mode === 'mat' && st.matSize) {
      var rec = up(st.matSize);
      return {
        snug: rec,
        tight: (rec === st.matSize) ? null : st.matSize,
        tightLabel: 'super tight fit',
        looseFirst: false,
        why: 'You wear ' + st.matSize + ' in emamaco maternity — recovery runs one size up.'
      };
    }
    var h = st.hip ? sizeFromBand(REC_HIP, toCm(st.hip)) : null;
    var usesWaist = (FAMILY === 'shapewear') || (st.stage === 'postpartum');
    var w = (usesWaist && st.waist) ? sizeFromBand(REC_WAIST, toCm(st.waist)) : null;
    // No tape measure: read her usual dress size straight off the recovery
    // chart's own AUS/US column. Measurements win when she has them.
    if (!h && !w && st.dress != null) {
      var d = sizeFromBand(isUS() ? REC_US : REC_AUS, st.dress);
      if (d) {
        var upD = up(d);
        return { snug: d, tight: (upD === d) ? null : upD, tightLabel: 'less tight fit',
                 looseFirst: true, why: 'A ' + st.dress + ' sits in ' + d + ' on the ' + (FAMILY === 'shapewear' ? 'shapewear' : 'recovery') + ' chart.' };
      }
    }
    if (!h && !w) return null;

    var base, why;
    if (h && w) {
      base = idx(w) > idx(h) ? w : h;
      why = 'Your waist is ' + w + ' and your hips are ' + h + ' — we go with the larger.';
    } else if (h) {
      base = h;
      why = 'Your ' + st.hip + unit() + ' hips sit in ' + h + '.';
    } else {
      base = w;
      why = 'Your ' + st.waist + unit() + ' waist sits in ' + w + '.';
    }
    var upper = up(base);
    return {
      snug: base,
      tight: (upper === base) ? null : upper,
      tightLabel: 'less tight fit',
      looseFirst: true,
      why: why
    };
  }

  /**
   * Crops / bras / nursing tops.
   *
   * Underbust is the input the page calls "the most accurate measurement to
   * use". Cup is a CONSTRAINT, not an input: each size holds up to a stated
   * cup, so a shopper whose cup exceeds what her underbust size holds must go
   * up. Larger of the two wins, same shape as the recovery waist/hip rule.
   *
   * The second option is one UP, framed as room rather than looseness —
   * breasts change a lot while feeding, and the returns notes say so directly
   * ("too tight to allow additional growth of breasts/chest area"). Crops skew
   * to too-small far less than leggings (2.5:1 vs 5.5:1), because a bra that's
   * too big fails too, so this deliberately does not push as hard.
   */
  function computedCrop() {
    // Above an F cup the chart simply doesn't cover her. Recommending off the
    // underbust alone would hand a G+ shopper an XS that holds a DD — the
    // precise mistake the cup column exists to prevent — so this stops and
    // says so instead of guessing.
    if (st.cup === 'G+') return { overCup: true };
    var fromU = st.underbust ? sizeFromUnderbust(toCm(st.underbust)) : null;
    var floor = st.cup ? CUP_FLOOR[st.cup] : null;
    // No tape measure: our crops fit true to size, so her usual top size is
    // the starting point. Underbust still wins when she has it — the page
    // calls it the most accurate measurement — and the cup floor applies to
    // either, since that is about what the garment can hold, not her frame.
    var fromUsual = (st.usual && CROP_SIZES.indexOf(st.usual) >= 0) ? st.usual : null;
    if (!fromU && !floor && !fromUsual) return null;
    if (!fromU) fromU = fromUsual;

    var ci = function (sz) { return CROP_SIZES.indexOf(sz); };
    var base, why;
    if (fromU && floor && ci(floor) > ci(fromU)) {
      base = floor;
      // fromU can come from the usual-size path, in which case there is no
      // measurement to quote — saying "your nullcm underbust" is worse than
      // saying nothing.
      why = (st.underbust
              ? 'Your ' + st.underbust + unit() + ' underbust is ' + fromU
              : fromU + ' would be your usual size') +
            ', but ' + fromU + ' only holds up to a ' + CROP_CUP_CAP[fromU] +
            ' cup — ' + base + ' holds your ' + st.cup + '.';
    } else if (fromU) {
      base = fromU;
      why = st.underbust
        ? 'Your ' + st.underbust + unit() + ' underbust sits in ' + base + '.'
        : 'Our crops fit true to size, so ' + base + ' it is.';
    } else {
      base = floor;
      why = base + ' holds up to a ' + CROP_CUP_CAP[base] + ' cup.';
    }

    var upper = CROP_SIZES[Math.min(CROP_SIZES.length - 1, ci(base) + 1)];
    return {
      snug: base,
      tight: (upper === base) ? null : upper,
      tightLabel: 'more room',
      looseFirst: true,
      why: why
    };
  }

  /**
   * General bottoms. Waist and hip, larger category wins — the same rule text
   * as recovery and shapewear. The pair differs: this page frames both sizes
   * as tight ("super tight fit or a tight fit"), so the alternative is one
   * DOWN and labelled accordingly, not one up.
   */
  function computedGeneral() {
    var gi = function (sz) { return GEN_SIZES.indexOf(sz); };
    var h = st.hip ? sizeFromBand(GEN_HIP, toCm(st.hip)) : null;
    var w = st.waist ? sizeFromBand(GEN_WAIST, toCm(st.waist)) : null;

    var base, why;
    if (h && w) {
      base = gi(w) > gi(h) ? w : h;
      why = 'Your waist is ' + w + ' and your hips are ' + h + ' — we go with the larger.';
    } else if (h) {
      base = h; why = 'Your ' + st.hip + unit() + ' hips sit in ' + h + '.';
    } else if (w) {
      base = w; why = 'Your ' + st.waist + unit() + ' waist sits in ' + w + '.';
    } else if (st.usual && GEN_SIZES.indexOf(st.usual) >= 0) {
      base = st.usual; why = 'These fit true to size, so ' + base + ' it is.';
    } else {
      return null;
    }
    var lower = GEN_SIZES[Math.max(0, gi(base) - 1)];
    return {
      snug: base,
      tight: (lower === base) ? null : lower,
      tightLabel: 'super tight fit',
      looseFirst: false,
      why: why
    };
  }

  function computed() {
    if (FAMILY === 'general') return computedGeneral();
    if (FAMILY === 'crop') return computedCrop();
    if (FAMILY === 'recovery' || FAMILY === 'shapewear') return computedRecovery();
    var fromHip = st.hip ? sizeFromHip(toCm(st.hip)) : null;
    var fromSaid = (st.mode === 'dress' && st.dress != null) ? sizeFromDress(st.dress) : (st.letter || null);
    if (!fromHip && !fromSaid) return null;

    var hipTxt = st.hip ? 'Your ' + st.hip + unit() + ' hips sit in ' + fromHip : '';
    var saidTxt = (st.mode === 'dress' && st.dress != null)
      ? 'a ' + st.dress + ' is ' + fromSaid
      : 'you wear ' + fromSaid + ' in leggings';

    if (fromHip && fromSaid && fromHip !== fromSaid) {
      // Adjacent is the chart's own overlap, so both genuinely fit and the
      // pair brackets them. Further apart than that and "both will fit" is
      // simply untrue — a mistyped measurement, or vanity sizing from another
      // brand. The tape is the objective one, so it wins, and the snug half
      // of it leads exactly as everywhere else.
      if (Math.abs(idx(fromHip) - idx(fromSaid)) > 1) {
        return {
          snug: fromHip,
          tight: down(fromHip),
          why: hipTxt + ' — we\'ve gone with your measurement rather than ' + fromSaid + '.'
        };
      }
      var hi = idx(fromHip) > idx(fromSaid) ? fromHip : fromSaid;
      var lo = hi === fromHip ? fromSaid : fromHip;
      return { snug: hi, tight: lo, why: hipTxt + ' and ' + saidTxt + ' — both will fit.' };
    }
    var base = fromHip || fromSaid;
    return {
      snug: base,
      tight: down(base),
      why: (st.hip ? hipTxt + '.' : saidTxt.charAt(0).toUpperCase() + saidTxt.slice(1) + '.')
    };
  }

  /**
   * Measurement control. A slider IS the primary input on a phone — nobody
   * types 120 into a spinner that steps by 1 — with the value shown large and
   * editable for anyone who wants to be exact.
   *
   * type="text" + inputmode="decimal", never type="number": number inputs
   * bring the spinner AND refuse setSelectionRange, so the caret cannot be
   * restored and typing comes out scrambled.
   */
  var MEAS_RANGE = {
    hip:       { cm: [70, 160], in: [28, 63] },
    waist:     { cm: [50, 130], in: [20, 51] },
    underbust: { cm: [55, 125], in: [22, 49] }
  };
  function measRow(key, label, value) {
    var r = MEAS_RANGE[key][unit()];
    var mid = Math.round((r[0] + r[1]) / 2);
    var has = (value != null && value !== '');
    return '<div class="emsg-meas" data-meas="' + key + '">' +
             '<div class="emsg-meas-top">' +
               '<span class="emsg-meas-lbl">' + esc(label) + '</span>' +
               '<span class="emsg-meas-val">' +
                 '<input id="emsg-' + key + '-num" class="emsg-num" type="text" inputmode="decimal" ' +
                   'autocomplete="off" data-meas-num="' + key + '" ' +
                   'placeholder="' + mid + '" value="' + (has ? value : '') + '">' +
                 '<span class="emsg-meas-unit">' + unit() + '</span>' +
               '</span>' +
             '</div>' +
             '<input id="emsg-' + key + '" class="emsg-range" type="range" data-meas-range="' + key + '" ' +
               'min="' + r[0] + '" max="' + r[1] + '" step="1" value="' + (has ? value : mid) + '" ' +
               'aria-label="' + esc(label) + '">' +
           '</div>';
  }

  var esc = function (s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); };

  function chipRow(values, active, attr) {
    return values.map(function (v) {
      var on = String(v) === String(active);
      return '<button type="button" class="emsg-chip' + (on ? ' emsg-on' : '') + '" data-' + attr + '="' + esc(v) + '">' + esc(v) + '</button>';
    }).join('');
  }

  /** Recovery view: maternity-size path by default, measurements behind a tap. */
  function renderRecovery(c, resultOnly) {
    var body = root.querySelector('#emsg-main');
    var q;
    if (st.mode === 'mat') {
      q = '<div class="emsg-q"><label>What size are your emamaco maternity leggings?</label>' +
            '<div class="emsg-chips">' + chipRow(SIZES, st.matSize, 'mat') + '</div>' +
            '<button type="button" class="emsg-link" id="emsg-mode-measure">I don\'t have emamaco maternity leggings</button>' +
          '</div>';
    } else {
      var sw = (FAMILY === 'shapewear');
      var pp = sw || st.stage === 'postpartum';
      var chip = function (v, label) {
        return '<button type="button" class="emsg-chip' + (st.stage === v ? ' emsg-on' : '') + '" data-stage="' + v + '">' + label + '</button>';
      };
      // Three stages, because plenty of people buy recovery well before 35
      // weeks. Under 35 and 35+ compute identically — hip alone, since a bump
      // makes a waist measurement meaningless either way — but they are split
      // so nobody has to claim a stage that isn't true, and so the early
      // cohort gets told their maternity size is the better guide.
      q = (sw ? '' :
          '<div class="emsg-q"><label>Where are you now?</label>' +
            '<div class="emsg-chips">' +
              chip('under35', 'under 35 weeks') + chip('pregnant', '35+ weeks') + chip('postpartum', 'postpartum') +
            '</div>' +
          '</div>') +
          '<div class="emsg-q"><label>' + (pp ? 'Hip and waist' : 'Hip measurement') + '</label>' +
            measRow('hip', 'Hip', st.hip) +
            (pp ? measRow('waist', 'Waist', st.waist) : '') +
            '<button type="button" class="emsg-link" id="emsg-video">watch how to measure</button>' +
            // Both fields need their own instruction — one line describing the
            // hip left the waist unexplained. The recovery page says to use
            // waist and hip but never says HOW to measure the waist, so that
            // line is standard garment guidance rather than lifted copy.
            '<div class="emsg-hint"><b>hip</b> — the widest part of your bum, not your bump</div>' +
            (pp ? '<div class="emsg-hint"><b>waist</b> — the narrowest part of your torso, usually just above your belly button</div>' +
                  '<div class="emsg-hint">We size by whichever of the two is larger.</div>' : '') +
            (st.stage === 'under35'
              ? '<div class="emsg-hint">Your hips change before your baby arrives, so this early it\'s a guide — your emamaco maternity size is the better one. Part of our 100-Day Perfect Fit Guarantee either way.</div>'
              : '') +
          '</div>' +
          '<div class="emsg-q"><label>No tape measure? <span>your usual dress size</span></label>' +
            '<div class="emsg-chips emsg-chips--sm">' +
              (function () {
                var T = isUS() ? REC_US : REC_AUS, seen = [];
                SIZES.forEach(function (sz) { T[sz].forEach(function (n) { if (seen.indexOf(n) < 0) seen.push(n); }); });
                seen.sort(function (a, b) { return a - b; });
                return seen.map(function (n) {
                  return '<button type="button" class="emsg-chip' + (st.dress === n ? ' emsg-on' : '') + '" data-dress="' + n + '">' + n + '</button>';
                }).join('');
              })() +
            '</div>' +
          '</div>' +
          (sw ? '' : '<button type="button" class="emsg-link" id="emsg-mode-mat">use my emamaco maternity size instead</button>');
    }

    var rec;
    if (c) {
      // Recommendation first, always. On the measurement path the alternative
      // is one UP ("less tight fit"); on the maternity-size path it is her own
      // maternity size, the brand's sanctioned "super tight fit".
      rec = '<div class="emsg-rec">' +
              '<div class="emsg-why">' + esc(c.why) + '</div>' +
              '<div class="emsg-head">You\'ll fit two sizes well</div>' +
              '<button type="button" class="emsg-opt' + (st.pick === c.snug ? ' emsg-opt--on' : '') + '" data-pick="' + c.snug + '">' +
                '<span class="emsg-size">' + c.snug + '</span>' +
                '<span class="emsg-opt-t"><b>your size</b>' + (!inStockOnPdp(c.snug) ? ' — sold out' : '') +
                '<em>What we recommend.</em></span>' +
              '</button>' +
              (c.tight ?
              '<button type="button" class="emsg-opt' + (st.pick === c.tight ? ' emsg-opt--on' : '') + '" data-pick="' + c.tight + '">' +
                '<span class="emsg-size">' + c.tight + '</span>' +
                '<span class="emsg-opt-t"><b>' + c.tightLabel + '</b>' + (!inStockOnPdp(c.tight) ? ' — sold out' : '') +
                '<em>' + (c.looseFirst ? 'A little more room.' : 'Same as your maternity size.') + '</em></span>' +
              '</button>' : '') +
            '</div>' +
            '<p class="emsg-note">These are a compression garment and quite tight — if you want a less tight fit you can size up, but don\'t size down. Part of our 100-Day Perfect Fit Guarantee.</p>';
    } else {
      rec = '<div class="emsg-rec emsg-rec--empty">Tell us your maternity size or your measurements and we\'ll show you the two sizes you\'ll fit.</div>';
    }
    if (resultOnly) { var _r = root.querySelector('#emsg-result'); if (_r) { _r.innerHTML = rec; return; } }
    body.innerHTML = q + '<div id="emsg-result">' + rec + '</div>';
  }

  /** General bottoms: usual size first, measurements optional. */
  function renderGeneral(c, resultOnly) {
    var body = root.querySelector('#emsg-main');
    var chips = GEN_SIZES.map(function (v) {
      return '<button type="button" class="emsg-chip' + (st.usual === v ? ' emsg-on' : '') + '" data-usual="' + v + '">' + v + '</button>';
    }).join('');

    var q =
      '<div class="emsg-q"><label>What size do you normally wear?</label>' +
        '<div class="emsg-chips">' + chips + '</div>' +
      '</div>' +
      '<div class="emsg-q"><label>Waist and hip <span>optional — no tape? skip it</span></label>' +
        measRow('waist', 'Waist', st.waist) +
        measRow('hip', 'Hip', st.hip) +
        '<div class="emsg-hint"><b>waist</b> — the narrowest part of your torso, usually just above your belly button</div>' +
        '<div class="emsg-hint"><b>hip</b> — the widest part of your bum</div>' +
        '<div class="emsg-hint">We size by whichever of the two is larger.</div>' +
      '</div>';

    var rec;
    if (c) {
      rec = '<div class="emsg-rec">' +
              '<div class="emsg-why">' + esc(c.why) + '</div>' +
              '<div class="emsg-head">Two sizes will fit you</div>' +
              '<button type="button" class="emsg-opt' + (st.pick === c.snug ? ' emsg-opt--on' : '') + '" data-pick="' + c.snug + '">' +
                '<span class="emsg-size">' + c.snug + '</span>' +
                '<span class="emsg-opt-t"><b>your size</b>' + (!inStockOnPdp(c.snug) ? ' — sold out' : '') +
                '<em>A tight fit. What most mums pick.</em></span>' +
              '</button>' +
              (c.tight ?
              '<button type="button" class="emsg-opt' + (st.pick === c.tight ? ' emsg-opt--on' : '') + '" data-pick="' + c.tight + '">' +
                '<span class="emsg-size">' + c.tight + '</span>' +
                '<span class="emsg-opt-t"><b>' + c.tightLabel + '</b>' + (!inStockOnPdp(c.tight) ? ' — sold out' : '') +
                '<em>Firmer again from the first wear.</em></span>' +
              '</button>' : '') +
            '</div>' +
            // Note this remedy is the OPPOSITE of the maternity one: the shaper
            // waistband is firmer, and its page says rolling or falling means
            // size UP, where the maternity page says the same symptom means
            // size down. Each range keeps its own instruction.
            '<p class="emsg-note">Try them on for an hour — keep the bag and leave the tags on. If the waistband rolls when you bend, or they fall at the crotch, size up. Part of our 100-Day Perfect Fit Guarantee.</p>';
    } else {
      rec = '<div class="emsg-rec emsg-rec--empty">Pick your usual size and we\'ll show you the two sizes that fit — add measurements to be sure.</div>';
    }
    if (resultOnly) { var _r = root.querySelector('#emsg-result'); if (_r) { _r.innerHTML = rec; return; } }
    body.innerHTML = q + '<div id="emsg-result">' + rec + '</div>';
  }

  function renderCrop(c, resultOnly) {
    var body = root.querySelector('#emsg-main');
    var cupChips = CUPS.map(function (v) {
      return '<button type="button" class="emsg-chip' + (st.cup === v ? ' emsg-on' : '') + '" data-cup="' + esc(v) + '">' + esc(v) + '</button>';
    }).join('');

    var usualChips = CROP_SIZES.map(function (v) {
      return '<button type="button" class="emsg-chip' + (st.usual === v ? ' emsg-on' : '') + '" data-usual="' + v + '">' + v + '</button>';
    }).join('');

    var q =
      '<div class="emsg-q"><label>What size top do you normally wear?</label>' +
        '<div class="emsg-chips">' + usualChips + '</div>' +
      '</div>' +
      '<div class="emsg-q"><label>Underbust <span>optional — no tape? skip it</span></label>' +
        measRow('underbust', 'Underbust', st.underbust) +
        '<div class="emsg-hint">just under the breasts, around the body — the most accurate measurement if you have a tape</div>' +
      '</div>' +
      '<div class="emsg-q"><label>Cup size <span>so we know it will hold you</span></label>' +
        '<div class="emsg-chips">' + cupChips + '</div>' +
      '</div>';

    var rec;
    if (c && c.overCup) {
      rec = '<div class="emsg-rec emsg-rec--empty">Our crops are made to hold up to an F cup. Above that we\'d rather help you properly than guess — get in touch and we\'ll find you the right fit.</div>';
    } else if (c) {
      rec = '<div class="emsg-rec">' +
              '<div class="emsg-why">' + esc(c.why) + '</div>' +
              '<button type="button" class="emsg-opt' + (st.pick === c.snug ? ' emsg-opt--on' : '') + '" data-pick="' + c.snug + '">' +
                '<span class="emsg-size">' + c.snug + '</span>' +
                '<span class="emsg-opt-t"><b>your size</b>' + (!inStockOnPdp(c.snug) ? ' — sold out' : '') +
                '<em>Holds up to a ' + CROP_CUP_CAP[c.snug] + ' cup.</em></span>' +
              '</button>' +
              (c.tight ?
              '<button type="button" class="emsg-opt' + (st.pick === c.tight ? ' emsg-opt--on' : '') + '" data-pick="' + c.tight + '">' +
                '<span class="emsg-size">' + c.tight + '</span>' +
                '<span class="emsg-opt-t"><b>' + c.tightLabel + '</b>' + (!inStockOnPdp(c.tight) ? ' — sold out' : '') +
                '<em>Room for your breasts to change while you feed.</em></span>' +
              '</button>' : '') +
            '</div>' +
            '<p class="emsg-note">Your size can change a lot while you\'re feeding. Part of our 100-Day Perfect Fit Guarantee.</p>';
    } else {
      rec = '<div class="emsg-rec emsg-rec--empty">Pick your usual top size and we\'ll show you your size — add a measurement or cup size to make it exact.</div>';
    }
    if (resultOnly) { var _r = root.querySelector('#emsg-result'); if (_r) { _r.innerHTML = rec; return; } }
    body.innerHTML = q + '<div id="emsg-result">' + rec + '</div>';
  }

  function render(resultOnly) {
    var c = computed();
    if (FAMILY === 'general') {
      renderGeneral(c, resultOnly);
      var ctaG = root.querySelector('#emsg-cta');
      if (c && st.pick) { ctaG.disabled = false; ctaG.textContent = 'SELECT SIZE ' + st.pick; }
      else { ctaG.disabled = true; ctaG.textContent = 'SELECT YOUR SIZE'; }
      return;
    }
    if (FAMILY === 'crop') {
      renderCrop(c, resultOnly);
      var ctaC = root.querySelector('#emsg-cta');
      if (c && st.pick) { ctaC.disabled = false; ctaC.textContent = 'SELECT SIZE ' + st.pick; }
      else { ctaC.disabled = true; ctaC.textContent = 'SELECT YOUR SIZE'; }
      return;
    }
    if (FAMILY === 'recovery' || FAMILY === 'shapewear') {
      renderRecovery(c, resultOnly);
      var ctaR = root.querySelector('#emsg-cta');
      if (c && st.pick) { ctaR.disabled = false; ctaR.textContent = 'SELECT SIZE ' + st.pick; }
      else { ctaR.disabled = true; ctaR.textContent = 'SELECT YOUR SIZE'; }
      return;
    }
    var body = root.querySelector('#emsg-main');
    var T = dressTable();
    var dressOpts = [];
    SIZES.forEach(function (s) { T[s].forEach(function (n) { if (dressOpts.indexOf(n) < 0) dressOpts.push(n); }); });
    dressOpts.sort(function (a, b) { return a - b; });

    var q1 = st.mode === 'letter'
      ? '<div class="emsg-chips">' + chipRow(SIZES, st.letter, 'letter') + '</div>' +
        '<button type="button" class="emsg-link" id="emsg-mode-dress">use my dress size instead</button>'
      : '<div class="emsg-chips emsg-chips--sm">' + chipRow(dressOpts, st.dress, 'dress') + '</div>' +
        '<button type="button" class="emsg-link" id="emsg-mode-letter">use my leggings size instead</button>';

    var rec = '';
    if (c) {
      var oos = !inStockOnPdp(c.snug);
      rec =
        '<div class="emsg-rec">' +
          '<div class="emsg-why">' + esc(c.why) + '</div>' +
          '<div class="emsg-head">You\'ll fit two sizes</div>' +
          '<button type="button" class="emsg-opt' + (st.pick === c.snug ? ' emsg-opt--on' : '') + '" data-pick="' + c.snug + '">' +
            '<span class="emsg-size">' + c.snug + '</span>' +
            '<span class="emsg-opt-t"><b>snug fit</b>' + (oos ? ' — sold out' : '') +
            '<em>What most mums pick. In most cases one size lasts your whole pregnancy.</em></span>' +
          '</button>' +
          (c.tight !== c.snug ?
          '<button type="button" class="emsg-opt' + (st.pick === c.tight ? ' emsg-opt--on' : '') + '" data-pick="' + c.tight + '">' +
            '<span class="emsg-size">' + c.tight + '</span>' +
            '<span class="emsg-opt-t"><b>tighter fit</b>' + (!inStockOnPdp(c.tight) ? ' — sold out' : '') +
            '<em>A firmer compression feel from day one.</em></span>' +
          '</button>' : '') +
        '</div>' +
        '<p class="emsg-note">They\'re a compression fabric, so they\'re meant to feel tight at first — after a couple of hours they\'ll shape and loosen with your body. Slipping at the crotch? Size down. Too restrictive? Size up. Part of our 100-Day Perfect Fit Guarantee.</p>';
    } else {
      rec = '<div class="emsg-rec emsg-rec--empty">Pick your usual size above and we\'ll show you the two sizes you\'ll fit.</div>';
    }

    var matBody =
      '<div class="emsg-q"><label>' +
        (st.mode === 'letter' ? 'What size leggings do you normally wear?' : 'What\'s your usual dress size?') +
      '</label>' + q1 + '</div>' +
      '<div class="emsg-q"><label>Hip measurement <span>optional — makes this exact</span></label>' +
        measRow('hip', 'Hip', st.hip) +
        '<button type="button" class="emsg-link" id="emsg-video">watch how to measure</button>' +
        '<div class="emsg-hint">the widest part of your hips, around the centre of your bum</div>' +
      '</div>' +
      '<div id="emsg-result">' + rec + '</div>';
    if (resultOnly) {
      var _rm = root.querySelector('#emsg-result');
      if (_rm) _rm.innerHTML = rec;
    } else {
      body.innerHTML = matBody;
    }

    var cta = root.querySelector('#emsg-cta');
    if (c && st.pick) { cta.disabled = false; cta.textContent = 'SELECT SIZE ' + st.pick; }
    else { cta.disabled = true; cta.textContent = 'SELECT YOUR SIZE'; }
  }

  function setPick(from) {
    var c = computed();
    st.pick = (c && c.snug) ? (from || c.snug) : null;
  }

  // ── sub-sheets ─────────────────────────────────────────────────────────
  function subSheet(title, inner) {
    var s = document.createElement('div');
    s.className = 'emsg-sub';
    s.innerHTML = '<div class="emsg-sub-card"><div class="emsg-sub-top"><b>' + esc(title) + '</b>' +
      '<button type="button" class="emsg-x" aria-label="Close">✕</button></div>' +
      '<div class="emsg-sub-body">' + inner + '</div></div>';
    s.addEventListener('click', function (e) {
      if (e.target === s || e.target.closest('.emsg-x')) {
        s.querySelectorAll('video').forEach(function (v) { try { v.pause(); } catch (err) {} });
        s.remove();
      }
    });
    root.appendChild(s);
    return s;
  }

  function openVideo() {
    subSheet('How to measure',
      '<video src="' + VIDEO.src + '" poster="' + VIDEO.poster + '" controls playsinline preload="none" style="width:100%;display:block;background:#000;"></video>' +
      '<p class="emsg-note" style="padding:12px 16px 4px;">Measure around the widest part of your hips, around the centre of your bum.</p>')
      .querySelector('video').play().catch(function () {});
  }

  function openChart() {
    if (FAMILY === 'general') {
      var T = isUS() ? GEN_US : GEN_AUS, lbl = isUS() ? 'US + CAN' : 'AUS + UK';
      var u = function (cm) { return isUS() ? Math.round(cm / 2.54) : cm; };
      var rowsG = GEN_SIZES.map(function (sz) {
        var d = T[sz];
        return '<tr><th>' + sz + '</th><td>' + (d[0] === d[1] ? d[0] : d[0] + '-' + d[1]) + '</td>' +
               '<td>' + u(GEN_WAIST[sz][0]) + '-' + u(GEN_WAIST[sz][1]) + '</td>' +
               '<td>' + u(GEN_HIP[sz][0]) + '-' + u(GEN_HIP[sz][1]) + '</td></tr>';
      }).join('');
      subSheet('Leggings + shorts size chart',
        '<table class="emsg-table"><thead><tr><th>Size</th><th>' + lbl + '</th><th>Waist (' + unit() + ')</th><th>Hip (' + unit() + ')</th></tr></thead><tbody>' + rowsG + '</tbody></table>' +
        '<p class="emsg-note" style="padding:10px 16px 4px;">Our leggings and shorts are engineered to compress and stretch. Our size chart is a guide and everyone is different.</p>');
      return;
    }
    if (FAMILY === 'crop') {
      var rowsC = CROP_SIZES.map(function (sz) {
        var u = function (cm) { return isUS() ? Math.round(cm / 2.54) : cm; };
        return '<tr><th>' + sz + '</th><td>' + u(CROP_BUST[sz]) + '</td><td>' + u(CROP_UNDERBUST[sz]) +
               '</td><td>up to ' + CROP_CUP_CAP[sz] + '</td></tr>';
      }).join('');
      subSheet('Crop + bra size chart',
        '<table class="emsg-table"><thead><tr><th>Size</th><th>Bust (' + unit() + ')</th><th>Underbust (' + unit() + ')</th><th>Cup</th></tr></thead><tbody>' + rowsC + '</tbody></table>' +
        '<p class="emsg-note" style="padding:10px 16px 4px;">The underbust is the most accurate measurement to use — just measure around the body just under the breasts.</p>');
      return;
    }
    var rec = (FAMILY === 'recovery' || FAMILY === 'shapewear');
    var T = rec ? (isUS() ? REC_US : REC_AUS) : dressTable();
    var HIPT = rec ? REC_HIP : HIP;
    var label = isUS() ? 'US + CAN' : 'AUS + UK';
    var toU = function (cm) { return isUS() ? Math.round(cm / 2.54) : cm; };
    var band = function (t, s, open) {
      var b = t[s];
      if (b[0] === b[1]) return String(b[0]);
      return open ? toU(b[0]) + '+' : toU(b[0]) + '-' + toU(b[1]);
    };
    var rows = SIZES.map(function (s) {
      var d = T[s];
      return '<tr><th>' + s + '</th><td>' + (d[0] === d[1] ? d[0] : d[0] + '-' + d[1]) + '</td>' +
        (rec ? '<td>' + band(REC_WAIST, s) + '</td>' : '') +
        '<td>' + band(HIPT, s, s === 'XXXL') + '</td></tr>';
    }).join('');
    subSheet(FAMILY === 'shapewear' ? 'Shapewear size chart' : rec ? 'Recovery size chart' : 'Leggings + shorts size chart',
      '<table class="emsg-table"><thead><tr><th>Size</th><th>' + label + '</th>' +
      (rec ? '<th>Waist (' + unit() + ')</th>' : '') +
      '<th>Hip (' + unit() + ')</th></tr></thead><tbody>' + rows + '</tbody></table>' +
      '<p class="emsg-note" style="padding:10px 16px 4px;">' +
      (FAMILY === 'shapewear'
        ? 'This chart is for Body Shapewear leggings — the Shaper Move range has its own. Our size chart is a guide and everyone and every body shape is different.'
        : rec
        ? 'The recovery chart is different to maternity and all other emamaco leggings. Our size chart is a guide and everyone and every body shape is different.'
        : 'Our size chart is a guide and everyone and every body shape is different. During pregnancy our bodies can change dramatically and some people may need 2 sizes to last their whole pregnancy.') +
      '</p>');
  }

  // ── open / close ───────────────────────────────────────────────────────
  var scrollY = 0;
  function close() {
    if (!root) return;
    root.remove(); root = null; st = null;
    document.body.style.position = ''; document.body.style.top = ''; document.body.style.width = '';
    window.scrollTo(0, scrollY);
    document.removeEventListener('keydown', onKey);
  }
  function onKey(e) {
    if (e.key !== 'Escape') return;
    var sub = root && root.querySelector('.emsg-sub');
    if (sub) sub.remove(); else close();
  }

  function open() {
    if (root) close();
    injectCss();
    scrollY = window.pageYOffset || document.documentElement.scrollTop;
    document.body.style.position = 'fixed';
    document.body.style.top = -scrollY + 'px';
    document.body.style.width = '100%';

    FAMILY = isCropProduct() ? 'crop'
           : isShapewearProduct() ? 'shapewear'
           : isRecoveryProduct() ? 'recovery'
           : isMaternityProduct() ? 'maternity'
           : isGeneralBottomProduct() ? 'general' : 'other';
    var sel = pdpSelectedSize();
    st = (FAMILY === 'general')
      ? { mode: 'general', usual: null, waist: null, hip: null, pick: null }
      : (FAMILY === 'crop')
      ? { mode: 'crop', usual: null, underbust: null, cup: null, pick: null }
      : (FAMILY === 'shapewear')
      ? { mode: 'measure', stage: 'postpartum', hip: null, waist: null, dress: null, pick: null }
      : (FAMILY === 'recovery')
      // Not prefilled from the PDP size: that is her RECOVERY size, and the
      // question asks for her MATERNITY size. Seeding it would put the wrong
      // number in front of her and then size it up again.
      ? { mode: 'mat', matSize: null, stage: 'under35', hip: null, waist: null, pick: null }
      : { mode: 'letter', letter: sel, dress: null, hip: null, pick: null };
    setPick();

    root = document.createElement('div');
    root.id = 'emsg';
    root.innerHTML =
      '<div class="emsg-card">' +
        '<div class="emsg-top"><b>' + (FAMILY === 'crop' ? 'Crop + bra sizing' : FAMILY === 'shapewear' ? 'Shapewear sizing' : FAMILY === 'recovery' ? 'Recovery sizing' : FAMILY === 'maternity' ? 'Maternity sizing' : FAMILY === 'general' ? 'Size guide' : 'Size guide') + '</b><button type="button" class="emsg-x" id="emsg-close" aria-label="Close">✕</button></div>' +
        '<div id="emsg-main"></div>' +
        '<div class="emsg-foot">' +
          '<button type="button" id="emsg-cta" disabled>SELECT YOUR SIZE</button>' +
          '<button type="button" class="emsg-link emsg-link--c" id="emsg-chart">full size chart</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(root);
    document.addEventListener('keydown', onKey);

    if (FAMILY === 'other') {
      // Mum-life sizes differently again; don't pretend otherwise.
      root.querySelector('#emsg-main').innerHTML =
        '<p class="emsg-note" style="margin:16px 0;">This guide covers our maternity and recovery bottoms and our crops. For the rest of the range, here\'s the chart.</p>';
      root.querySelector('#emsg-cta').style.display = 'none';
      root.querySelector('#emsg-chart').addEventListener('click', openChart);
      root.addEventListener('click', function (e) { if (e.target === root || e.target.closest('#emsg-close')) close(); });
      return;
    }

    render();

    root.addEventListener('click', function (e) {
      var t = e.target;
      if (t === root || t.closest('#emsg-close')) { close(); return; }
      if (t.closest('#emsg-video')) { openVideo(); return; }
      if (t.closest('#emsg-chart')) { openChart(); return; }
      if (t.closest('#emsg-mode-dress')) { st.mode = 'dress'; render(); return; }
      if (t.closest('#emsg-mode-letter')) { st.mode = 'letter'; render(); return; }
      if (t.closest('#emsg-mode-measure')) { st.mode = 'measure'; setPick(); render(); return; }
      if (t.closest('#emsg-mode-mat')) { st.mode = 'mat'; setPick(); render(); return; }
      var stg = t.closest('[data-stage]');
      if (stg) {
        st.stage = stg.getAttribute('data-stage');
        if (st.stage !== 'postpartum') st.waist = null;   // hip alone while pregnant, either stage
        setPick(); render(); return;
      }
      var usual = t.closest('[data-usual]');
      if (usual) { st.usual = usual.getAttribute('data-usual'); setPick(); render(); return; }
      var cup = t.closest('[data-cup]');
      if (cup) { st.cup = cup.getAttribute('data-cup'); setPick(); render(); return; }
      var chip = t.closest('[data-letter],[data-dress],[data-mat]');
      if (chip) {
        if (chip.hasAttribute('data-mat')) st.matSize = chip.getAttribute('data-mat');
        else if (chip.hasAttribute('data-letter')) st.letter = chip.getAttribute('data-letter');
        else st.dress = Number(chip.getAttribute('data-dress'));
        setPick(); render(); return;
      }
      var opt = t.closest('[data-pick]');
      if (opt) { st.pick = opt.getAttribute('data-pick'); render(); return; }
      if (t.closest('#emsg-cta')) {
        if (st.pick) { selectOnPdp(st.pick); close(); }
      }
    });

    /**
     * Measurement input. Deliberately does NOT call the full render: that
     * replaced the very element being typed into, and because a number input
     * refuses setSelectionRange the caret could not be put back — so the
     * cursor snapped to position 0 and "120" came out as "21". Selecting and
     * deleting broke for the same reason. Only the result block repaints now;
     * the slider and its readout are never rebuilt mid-interaction.
     */
    root.addEventListener('input', function (e) {
      var key = e.target.getAttribute && (e.target.getAttribute('data-meas-num') || e.target.getAttribute('data-meas-range'));
      if (!key) return;
      var raw = String(e.target.value || '').replace(/[^0-9.]/g, '');
      var v = parseFloat(raw);
      st[key] = isNaN(v) ? null : v;

      var row = root.querySelector('[data-meas="' + key + '"]');
      if (row) {
        var num = row.querySelector('[data-meas-num]');
        var rng = row.querySelector('[data-meas-range]');
        // Mirror one control into the other, never the one being used.
        if (e.target === rng && num) num.value = raw;
        else if (e.target === num && rng && !isNaN(v)) rng.value = v;
        if (row.parentNode) row.classList.toggle('emsg-meas--set', !isNaN(v));
      }
      setPick();
      render(true);
    });
  }

  function injectCss() {
    if (document.getElementById('emsg-css')) return;
    var s = document.createElement('style');
    s.id = 'emsg-css';
    s.textContent =
      '#emsg{position:fixed;inset:0;z-index:100001;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;padding:12px;font-family:system-ui,-apple-system,sans-serif;}' +
      '.emsg-card{background:#fff;width:100%;max-width:520px;max-height:88vh;max-height:88dvh;overflow-y:auto;border-radius:14px;}' +
      '.emsg-top{display:flex;justify-content:space-between;align-items:center;padding:12px 16px;border-bottom:1px solid #e5e7eb;}' +
      '.emsg-top b{font-size:15px;color:#1f2937;}' +
      '.emsg-x{background:none;border:none;font-size:19px;line-height:1;color:#374151;cursor:pointer;padding:2px 4px;}' +
      '#emsg-main{padding:12px 16px 0;}' +
      '.emsg-q{margin:0 0 12px;}' +
      '.emsg-q label{display:block;font-size:13px;font-weight:700;color:#1f2937;margin:0 0 6px;}' +
      '.emsg-q label span{font-weight:400;color:#9ca3af;}' +
      '.emsg-chips{display:flex;gap:4px;}' +
      '.emsg-chips--sm{flex-wrap:wrap;gap:6px;}' + '.emsg-chips--sm .emsg-chip{flex:0 1 auto;min-width:40px;font-size:13px;padding:9px 6px;}' +
      '.emsg-chip{flex:1 1 0;min-width:0;padding:9px 1px;font-size:12px;font-weight:700;letter-spacing:-.02em;color:#1f2937;background:#fff;border:1px solid #d1d5db;border-radius:0;cursor:pointer;font-family:inherit;}' +
      '.emsg-chip.emsg-on{background:#1f2937;color:#fff;border-color:#1f2937;}' +
      '.emsg-link{display:inline-block;margin:6px 0 0;padding:0;background:none;border:none;font-size:12px;color:#6b7280;text-decoration:underline;cursor:pointer;font-family:inherit;}' +
      '.emsg-link--c{display:block;margin:6px auto 0;}' +
      '.emsg-hiprow{display:flex;align-items:center;gap:6px;flex-wrap:wrap;}' +
      // Measurement control: slider primary, value typeable, no spinners.
      '.emsg-meas{margin:0 0 10px;}' +
      '.emsg-meas-top{display:flex;align-items:baseline;justify-content:space-between;margin:0 0 4px;}' +
      '.emsg-meas-lbl{font-size:12px;font-weight:700;color:#1f2937;}' +
      '.emsg-meas-val{display:inline-flex;align-items:baseline;gap:3px;}' +
      '.emsg-num{width:58px;padding:2px 4px;font-size:19px;font-weight:800;color:#1f2937;text-align:right;border:0;border-bottom:1px solid #d1d5db;border-radius:0;background:none;font-family:inherit;-webkit-appearance:none;appearance:none;}' +
      '.emsg-num:focus{outline:none;border-bottom-color:#1f2937;}' +
      '.emsg-num::placeholder{color:#d1d5db;font-weight:400;}' +
      '.emsg-meas-unit{font-size:12px;color:#6b7280;}' +
      '.emsg-range{-webkit-appearance:none;appearance:none;width:100%;height:26px;background:none;margin:0;display:block;}' +
      '.emsg-range::-webkit-slider-runnable-track{height:4px;background:#e5e7eb;border-radius:2px;}' +
      '.emsg-range::-moz-range-track{height:4px;background:#e5e7eb;border-radius:2px;}' +
      '.emsg-range::-webkit-slider-thumb{-webkit-appearance:none;width:26px;height:26px;margin-top:-11px;border-radius:50%;background:#1f2937;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.3);cursor:pointer;}' +
      '.emsg-range::-moz-range-thumb{width:22px;height:22px;border-radius:50%;background:#1f2937;border:2px solid #fff;cursor:pointer;}' +
      // (the old #emsg-hip/#emsg-waist/#emsg-underbust text-box rule is gone:
      //  those ids now belong to the RANGE inputs, and an id selector beat
      //  .emsg-range, boxing the slider into a 74px bordered stub)
      '.emsg-unit{font-size:13px;color:#6b7280;}' +
      '.emsg-hiprow .emsg-link{margin:0 0 0 auto;}' +
      '.emsg-hint{font-size:11px;color:#9ca3af;margin-top:3px;line-height:1.35;}' + '.emsg-hint b{color:#6b7280;font-weight:700;}' +
      '.emsg-rec{border-top:1px solid #e5e7eb;padding-top:10px;}' +
      '.emsg-rec--empty{font-size:13px;color:#9ca3af;padding:14px 0;}' +
      '.emsg-why{font-size:12px;color:#6b7280;margin-bottom:4px;}' +
      '.emsg-head{font-size:13px;font-weight:700;color:#1f2937;margin-bottom:6px;}' +
      '.emsg-opt{display:flex;align-items:center;gap:10px;width:100%;text-align:left;padding:8px 10px;margin-bottom:6px;background:#fff;border:1px solid #d1d5db;border-radius:0;cursor:pointer;font-family:inherit;}' +
      '.emsg-opt--on{border-color:#1f2937;box-shadow:inset 0 0 0 1px #1f2937;}' +
      '.emsg-size{flex:0 0 auto;min-width:38px;text-align:center;font-size:17px;font-weight:800;color:#1f2937;}' +
      '.emsg-opt-t{display:block;font-size:12px;color:#1f2937;}' +
      '.emsg-opt-t b{display:block;font-size:12px;}' +
      '.emsg-opt-t em{display:block;font-style:normal;color:#6b7280;font-size:11px;line-height:1.35;margin-top:1px;}' +
      '.emsg-note{font-size:11px;line-height:1.45;color:#6b7280;margin:8px 0 0;}' +
      '.emsg-foot{padding:10px 16px 14px;}' +
      '#emsg-cta{width:100%;padding:13px;background:#1f2937;color:#fff;border:none;border-radius:4px;font-size:13px;font-weight:800;letter-spacing:.08em;cursor:pointer;font-family:inherit;}' +
      '#emsg-cta:disabled{background:#d1d5db;cursor:default;}' +
      '.emsg-sub{position:fixed;inset:0;z-index:100002;background:rgba(0,0,0,.6);display:flex;align-items:center;justify-content:center;padding:12px;}' +
      '.emsg-sub-card{background:#fff;width:100%;max-width:520px;max-height:84vh;max-height:84dvh;overflow-y:auto;border-radius:14px;}' +
      '.emsg-sub-top{display:flex;justify-content:space-between;align-items:center;padding:12px 16px;border-bottom:1px solid #e5e7eb;font-size:15px;color:#1f2937;}' +
      '.emsg-table{width:100%;border-collapse:collapse;font-size:13px;}' +
      '.emsg-table th,.emsg-table td{padding:7px 10px;border-bottom:1px solid #f3f4f6;text-align:left;color:#1f2937;}' +
      '.emsg-table thead th{font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:#6b7280;}';
    document.head.appendChild(s);
  }

  window.openEmamacoSizeGuide = open;
  // Same event the retired Convomesh widget listened for, so the Size Guide
  // link and stage-set.js need no changes.
  window.addEventListener('convomesh-open-sizing', open);
})();
